import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import pedidoVentaService, { PedidoVenta, PedidoVentaDetalle } from '../../services/pedidoVentaService';
import { formatClienteConNro, formatNroDocumentoVenta } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import { PEDIDO_ESTADOS } from '../../utils/pedidoVentaEstados';
import {
  pendientesAccionCellSx,
  pendientesArticuloCellSx,
  pendientesTableSx,
} from './pendientesTableSx';

export interface LineaPedidoEnEspera {
  nroFormularioPes: number;
  nroItemPes: number;
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
}

interface IncorporarPedidoEnEsperaDialogProps {
  open: boolean;
  onClose: () => void;
  lineasEnOrden: { nroFormularioPes?: number; nroItemPes?: number }[];
  onAgregar: (linea: LineaPedidoEnEspera) => void;
}

interface FilaEspera extends LineaPedidoEnEspera {
  comprobante: string;
  cliente: string;
  fecha: string;
}

const comprobanteDe = (pedido: PedidoVenta): string => {
  const nro = formatNroDocumentoVenta(
    (pedido as { letra?: string }).letra,
    (pedido as { nroSuc?: number }).nroSuc,
    (pedido as { nroComprobante?: number }).nroComprobante,
  );
  return nro || String(pedido.nroFormulario || '');
};

const filasDePedido = (pedido: PedidoVenta): FilaEspera[] => {
  const comprobante = comprobanteDe(pedido);
  const cliente = formatClienteConNro(pedido.nroCliente, pedido.nombre);
  const fecha = pedido.fechaEmision ? new Date(pedido.fechaEmision).toLocaleDateString('es-AR') : '—';
  return (pedido.detalles ?? [])
    .map((detalle: PedidoVentaDetalle, index) => {
      const codigo = String(detalle.codigoArticulo ?? '').trim();
      const cantidadLinea = Number(detalle.cantidad) || 0;
      const cantidadEspera = Number(detalle.cantidadesPES) || 0;
      const cantidad = cantidadLinea > 0 ? cantidadLinea : cantidadEspera;
      if (!codigo || cantidad <= 0) return null;
      return {
        nroFormularioPes: pedido.nroFormulario,
        nroItemPes: Number(detalle.nroItem) > 0 ? Number(detalle.nroItem) : index + 1,
        codigoArticulo: codigo,
        descripcion: String(detalle.descripcionArticulo ?? '').trim(),
        cantidad,
        precioUnitario: Number(detalle.precioUnitario) || 0,
        comprobante,
        cliente,
        fecha,
      };
    })
    .filter((fila): fila is FilaEspera => fila != null);
};

const estaAgregada = (
  fila: FilaEspera,
  lineasEnOrden: { nroFormularioPes?: number; nroItemPes?: number }[],
) =>
  lineasEnOrden.some(
    (linea) => linea.nroFormularioPes === fila.nroFormularioPes && linea.nroItemPes === fila.nroItemPes,
  );

const IncorporarPedidoEnEsperaDialog: React.FC<IncorporarPedidoEnEsperaDialogProps> = ({
  open,
  onClose,
  lineasEnOrden,
  onAgregar,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const [filas, setFilas] = useState<FilaEspera[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    pedidoVentaService
      .getPedidosEnEspera({ pageNumber: 1, pageSize: 20 })
      .then(async (response) => {
        const pedidos = (response.items ?? []).filter((pedido) => pedido.estado !== PEDIDO_ESTADOS.ANULADO);
        const completos = await Promise.all(
          pedidos.map(async (pedido) => {
            if ((pedido.detalles ?? []).length > 0) return pedido;
            try {
              return await pedidoVentaService.getPedidoVenta(pedido.nroFormulario);
            } catch {
              return pedido;
            }
          }),
        );
        if (!cancelled) setFilas(completos.flatMap(filasDePedido));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setFilas([]);
        enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron cargar los pedidos en espera'), {
          variant: 'error',
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, enqueueSnackbar]);

  const accion = (fila: FilaEspera, fullWidth = false) =>
    estaAgregada(fila, lineasEnOrden) ? (
      <Button size="small" disabled fullWidth={fullWidth}>
        Agregado
      </Button>
    ) : (
      <Button
        size="small"
        fullWidth={fullWidth}
        variant={fullWidth ? 'contained' : 'text'}
        onClick={() => onAgregar(fila)}
        sx={fullWidth ? { textTransform: 'none', fontWeight: 600, borderRadius: 2 } : undefined}
      >
        Agregar
      </Button>
    );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1, pb: 1 }}
      >
        <Typography component="span" variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
          Pedidos en espera
        </Typography>
        <IconButton aria-label="Cerrar" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
        {loading ? (
          <CircularProgress size={22} sx={{ display: 'block', my: 2 }} />
        ) : filas.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No hay líneas de pedidos en espera.
          </Typography>
        ) : isMobile ? (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {filas.map((fila) => (
              <Card key={`${fila.nroFormularioPes}-${fila.nroItemPes}`} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                        {fila.comprobante}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {fila.nroFormularioPes}/{fila.nroItemPes}
                      </Typography>
                    </Stack>
                    <Box
                      sx={(t) => ({
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: t.palette.primary.light + '14',
                      })}
                    >
                      <Typography variant="caption" color="text.secondary" display="block">
                        Artículo
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', wordBreak: 'break-word' }}>
                        {fila.codigoArticulo} - {fila.descripcion}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Emisión: {fila.fecha}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fila.cliente}
                    </Typography>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Cantidad
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {fila.cantidad}
                      </Typography>
                    </Box>
                    {accion(fila, true)}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <TableContainer sx={{ mt: 1, maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={pendientesTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Master</TableCell>
                  <TableCell>Comprobante</TableCell>
                  <TableCell>Fecha emisión</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Artículo</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell align="center" sx={pendientesAccionCellSx}>
                    Acción
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filas.map((fila) => (
                  <TableRow key={`${fila.nroFormularioPes}-${fila.nroItemPes}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {fila.nroFormularioPes}/{fila.nroItemPes}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fila.comprobante}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fila.fecha}</TableCell>
                    <TableCell>{fila.cliente}</TableCell>
                    <TableCell sx={pendientesArticuloCellSx}>
                      {fila.codigoArticulo} - {fila.descripcion}
                    </TableCell>
                    <TableCell align="right">{fila.cantidad}</TableCell>
                    <TableCell align="center" sx={pendientesAccionCellSx}>
                      {accion(fila)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} fullWidth={isMobile} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncorporarPedidoEnEsperaDialog;
